import { FC, useState, useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Link } from "@mui/material";
import DocumentTitle from "../../components/documentTitle/DocumentTitle";
import { CONSTANTS } from "../../../utils/CONSTS";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Class as ClassIcon,
} from "@mui/icons-material";
import MainTitle from "../../components/mainTitle";
import {
  useClassHook,
  useDeleteClassHook,
  useSyllabusHook,
} from "../../../hooks/useAcademicHook";
import { TClass } from "../../../apis/academic/academic.interfaces";
import { useInstituteContext } from "../../../context/institute.context";
import { logger, pages } from "../../../utils/common";
import { useCourseHook } from "../../../hooks/useAcademicHook";
import ManageClassDialog from "./manageClassDialog";
import { hasCoursePrerequisite } from "./academic.helpers";
import { Info as InfoIcon } from "@mui/icons-material";

const ClassListView: FC = () => {
  const { selectedInstitute } = useInstituteContext();
  const { data: classes, isLoading, error } = useClassHook();
  const { data: courses, isLoading: coursesLoading } = useCourseHook();
  const { data: syllabi } = useSyllabusHook();
  const deleteClassMutation = useDeleteClassHook();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [classToDelete, setClassToDelete] = useState<TClass | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [classToEdit, setClassToEdit] = useState<TClass | null>(null);

  const hasCourses = hasCoursePrerequisite(courses);

  const sortedClasses = useMemo(() => {
    if (!classes) return [];
    return [...classes].sort((a, b) =>
      a.grade.localeCompare(b.grade, undefined, { numeric: true }),
    );
  }, [classes]);

  const getCourseName = (courseId: string): string => {
    return courses?.find((c) => c.id === courseId)?.name ?? courseId;
  };

  const handleAddClass = () => {
    setClassToEdit(null);
    setEditDialogOpen(true);
  };

  const handleEditClass = (classItem: TClass) => {
    setClassToEdit(classItem);
    setEditDialogOpen(true);
  };

  const handleCloseManageDialog = () => {
    setEditDialogOpen(false);
    setClassToEdit(null);
  };

  const handleDeleteClass = (classItem: TClass) => {
    setClassToDelete(classItem);
    setDeleteError(null);

    // Check dependencies
    const dependentSyllabi =
      syllabi?.filter((s) => s.classId === classItem.id) || [];

    if (dependentSyllabi.length > 0) {
      setDeleteError(
        `Cannot delete class. ${dependentSyllabi.length} syllabus/syllabi are assigned to this class. Please remove these dependencies first.`,
      );
    }

    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!classToDelete) return;

    if (deleteError) {
      setDeleteDialogOpen(false);
      setClassToDelete(null);
      setDeleteError(null);
      return;
    }

    try {
      await deleteClassMutation.mutateAsync(classToDelete.id);
      logger.show_success("Class deleted successfully!");
      setDeleteDialogOpen(false);
      setClassToDelete(null);
      setDeleteError(null);
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : "Failed to delete class. Please try again.",
      );
    }
  };

  if (!selectedInstitute) {
    return (
      <>
        <DocumentTitle title={`${CONSTANTS?.title?.academics ? CONSTANTS?.title?.academics + ' - ' : ''} Classes`} />
        <Box p={3} data-testid="class-list-view">
          <MainTitle title="Classes/Standards" />
          <Alert severity="info" data-testid="class-list-no-institute">
            Please select an institute from the dashboard to view classes.
          </Alert>
        </Box>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <DocumentTitle title={`${CONSTANTS?.title?.academics ? CONSTANTS?.title?.academics + ' - ' : ''} Classes`} />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          data-testid="class-list-view"
        >
          <CircularProgress data-testid="class-list-loading" />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <DocumentTitle title={`${CONSTANTS?.title?.academics ? CONSTANTS?.title?.academics + ' - ' : ''} Classes`} />
        <Box p={3} data-testid="class-list-view">
          <MainTitle title="Classes/Standards" />
          <Alert severity="error" data-testid="class-list-error">
            Failed to load classes. Please try again later.
          </Alert>
        </Box>
      </>
    );
  }

  // Check if courses are still loading
  if (coursesLoading) {
    return (
      <>
        <DocumentTitle title={`${CONSTANTS?.title?.academics ? CONSTANTS?.title?.academics + ' - ' : ''} Classes`} />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          data-testid="class-list-view"
        >
          <CircularProgress data-testid="class-list-loading-courses" />
        </Box>
      </>
    );
  }

  // Show empty state if no courses exist and no classes exist
  // If classes exist, show them but disable add button
  const showEmptyState = !hasCourses && sortedClasses.length === 0;

  return (
    <>
      <DocumentTitle title={`${CONSTANTS?.title?.academics ? CONSTANTS?.title?.academics + ' - ' : ''} Classes`} />

      <Grid container spacing={3} data-testid="class-list-view">
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}
            data-testid="class-list-header"
          >
            <MainTitle title="Classes/Standards" />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddClass}
              size="large"
              disabled={!hasCourses}
              title={
                !hasCourses
                  ? "To create classes, you must first set up courses in the Courses tab"
                  : ""
              }
              data-testid="add-class-button"
            >
              Add Class
            </Button>
          </Box>
        </Grid>

        {showEmptyState ? (
          <Grid item xs={12}>
            <Card data-testid="class-prerequisite-empty-state">
              <CardContent>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  minHeight="200px"
                  gap={2}
                  data-testid="class-prerequisite-empty-content"
                >
                  <InfoIcon sx={{ fontSize: 64, color: "info.main" }} />
                  <Typography variant="h6" color="text.secondary">
                    No courses configured
                  </Typography>
                  <Alert
                    severity="info"
                    sx={{ maxWidth: 600, width: "100%" }}
                    data-testid="class-prerequisite-message"
                  >
                    To create classes, you must first set up courses in the
                    Courses tab.
                  </Alert>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ) : sortedClasses.length === 0 ? (
          <Grid item xs={12}>
            <Card data-testid="class-empty-state">
              <CardContent>
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  minHeight="200px"
                  gap={2}
                  data-testid="class-empty-content"
                >
                  <ClassIcon sx={{ fontSize: 64, color: "text.secondary" }} />
                  <Typography variant="h6" color="text.secondary">
                    No classes configured
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Get started by adding your first class.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddClass}
                    disabled={!hasCourses}
                    title={
                      !hasCourses
                        ? "To create classes, you must first set up courses in the Courses tab"
                        : ""
                    }
                    data-testid="add-class-empty-button"
                  >
                    Add Class
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          <Grid item xs={12}>
            <TableContainer
              component={Paper}
              data-testid="class-table-container"
            >
              <Table data-testid="class-table">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Grade</TableCell>
                    <TableCell>Course</TableCell>
                    <TableCell>Academic year</TableCell>
                    <TableCell>Open</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedClasses.map((classItem) => (
                    <TableRow
                      key={classItem.id}
                      hover
                      data-testid={`class-row-${classItem.id}`}
                    >
                      <TableCell>
                        <Link
                          component={RouterLink}
                          to={`${pages.academics.path}/classes/${classItem.id}`}
                          underline="hover"
                          fontWeight="medium"
                          data-testid={`open-class-hub-${classItem.id}`}
                        >
                          {classItem.name}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={classItem.grade}
                          variant="outlined"
                          size="small"
                          data-testid={`class-level-${classItem.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {getCourseName(classItem.courseId)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {classItem.academicYear}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Link
                          component={RouterLink}
                          to={`${pages.academics.path}/classes/${classItem.id}`}
                          variant="body2"
                          data-testid={`class-hub-link-${classItem.id}`}
                        >
                          Class hub
                        </Link>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleEditClass(classItem)}
                          color="primary"
                          data-testid={`edit-class-${classItem.id}`}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClass(classItem)}
                          color="error"
                          data-testid={`delete-class-${classItem.id}`}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}
      </Grid>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setClassToDelete(null);
          setDeleteError(null);
        }}
        data-testid="delete-class-dialog"
      >
        <DialogTitle data-testid="delete-class-dialog-title">
          Delete Class
        </DialogTitle>
        <DialogContent data-testid="delete-class-dialog-content">
          {deleteError ? (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              data-testid="delete-class-error"
            >
              {deleteError}
            </Alert>
          ) : null}
          <DialogContentText>
            Are you sure you want to delete class "{classToDelete?.name}"? This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions data-testid="delete-class-dialog-actions">
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setClassToDelete(null);
            }}
            disabled={deleteClassMutation.isPending}
            data-testid="cancel-delete-class-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteClassMutation.isPending || !!deleteError}
            startIcon={
              deleteClassMutation.isPending ? (
                <CircularProgress size={16} />
              ) : (
                <DeleteIcon />
              )
            }
            data-testid="confirm-delete-class-button"
          >
            {deleteClassMutation.isPending
              ? "Deleting..."
              : deleteError
                ? "Close"
                : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <ManageClassDialog
        open={editDialogOpen}
        onClose={handleCloseManageDialog}
        classItem={classToEdit}
      />
    </>
  );
};

export default ClassListView;
